/**
 * Override Re-Review Queue — Phase 1c
 * ────────────────────────────────────
 * Per Lock #1: extends P12.5 PlanningObservationEntry metadata with an
 * optional `overrideOutcome` field. NO new model. NO new storage.
 *
 * The re-review queue is a derived selector over P12.5 observations
 * where a manual override happened, but no outcome has been recorded
 * yet (or where the recorded outcome is "negative" — flagged for
 * reviewer attention).
 *
 * In SOFT mode: queue surfaces the items, reviewer manually classifies.
 * In HARD mode: queue + auto-flag negative outcomes for re-review.
 * Both modes use the same writer below.
 */

export type OverrideOutcomeKind = "positive" | "neutral" | "negative" | "pending";

export interface OverrideOutcomeRecord {
  /** Links to the PlanningObservationEntry (P12.5) by id. */
  observationId: string;
  jobId: string;
  outcome: OverrideOutcomeKind;
  reviewerId: string;
  reviewerRole: string;
  reason: string;
  recordedAt: string;
  enforcementModeAtWrite: "soft" | "hard";
}

const FORBIDDEN_KEYS = ["contractValue", "customerEmail", "customerPhone", "ssn"] as const;

function sanitize<T extends Record<string, unknown>>(payload: T): T {
  const out = { ...payload };
  for (const key of FORBIDDEN_KEYS) {
    if (key in out) delete (out as Record<string, unknown>)[key];
  }
  return out;
}

import { getNetworkEnforcementMode } from "./enforcementMode";

export interface RecordOverrideOutcomeInput {
  records: OverrideOutcomeRecord[];
  observationId: string;
  jobId: string;
  outcome: OverrideOutcomeKind;
  reviewerId: string;
  reviewerRole: string;
  reason: string;
  nowIso?: string;
}

export interface RecordOverrideOutcomeResult {
  records: OverrideOutcomeRecord[];
  record: OverrideOutcomeRecord | null;
  error?: string;
}

/**
 * Append-only writer. The same observation can be re-reviewed multiple
 * times (e.g. flipped from "pending" → "negative" → "neutral" after
 * additional info). The latest entry per observationId wins on read.
 */
export function recordOverrideOutcome(
  input: RecordOverrideOutcomeInput,
): RecordOverrideOutcomeResult {
  if (!input.reviewerId) return { records: input.records, record: null, error: "missing_reviewer" };
  if (!input.reason || input.reason.trim().length < 10) {
    return { records: input.records, record: null, error: "reason_too_short" };
  }
  const sanitizedReason = sanitize({ reason: input.reason }).reason ?? "";
  const record: OverrideOutcomeRecord = {
    observationId: input.observationId,
    jobId: input.jobId,
    outcome: input.outcome,
    reviewerId: input.reviewerId,
    reviewerRole: input.reviewerRole,
    reason: sanitizedReason.trim(),
    recordedAt: input.nowIso ?? new Date().toISOString(),
    enforcementModeAtWrite: getNetworkEnforcementMode(),
  };
  return { records: [...input.records, record], record };
}

/** Latest outcome per observationId. Returns "pending" sentinel if none recorded. */
export function getLatestOverrideOutcome(
  records: OverrideOutcomeRecord[],
  observationId: string,
): OverrideOutcomeRecord | null {
  const own = records.filter((r) => r.observationId === observationId);
  if (own.length === 0) return null;
  return own.sort((a, b) => Date.parse(b.recordedAt) - Date.parse(a.recordedAt))[0];
}

export interface OverrideRereviewQueueItem {
  observationId: string;
  jobId: string;
  currentOutcome: OverrideOutcomeKind;
  lastRecordedAt: string | null;
  reason: "never_reviewed" | "negative_outcome";
}

/**
 * Items that need a reviewer's attention. Caller passes the set of
 * P12.5 observations that involved an override (the resolver already
 * filters for those). This selector then classifies them.
 */
export function getOverrideRereviewQueue(
  overrideObservationIds: Array<{ observationId: string; jobId: string }>,
  records: OverrideOutcomeRecord[],
): OverrideRereviewQueueItem[] {
  const out: OverrideRereviewQueueItem[] = [];
  for (const obs of overrideObservationIds) {
    const latest = getLatestOverrideOutcome(records, obs.observationId);
    if (!latest) {
      out.push({
        observationId: obs.observationId,
        jobId: obs.jobId,
        currentOutcome: "pending",
        lastRecordedAt: null,
        reason: "never_reviewed",
      });
      continue;
    }
    if (latest.outcome === "negative") {
      out.push({
        observationId: obs.observationId,
        jobId: obs.jobId,
        currentOutcome: "negative",
        lastRecordedAt: latest.recordedAt,
        reason: "negative_outcome",
      });
    }
  }
  return out;
}
